import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { extractTime, formatDate, formatHours, formatMinutes, getErrorMessage } from "../lib/formatters";


const managerNavItems = [
  { path: "/manager/dashboard", label: "Manager Dashboard", shortLabel: "HOME", testId: "manager-dashboard" },
  { path: "/manager/team", label: "Team Attendance", shortLabel: "TEAM", testId: "manager-team" },
  { path: "/manager/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "manager-approvals" },
  { path: "/manager/reports", label: "Reports", shortLabel: "REPORTS", testId: "manager-reports" },
];

const managerSelfServiceNav = [
  { path: "/employee/dashboard", label: "My Dashboard", shortLabel: "SELF", testId: "manager-self-dashboard-nav" },
  { path: "/employee/attendance", label: "My Attendance", shortLabel: "TIME", testId: "manager-self-attendance-nav" },
  { path: "/employee/leave", label: "My Leave", shortLabel: "LEAVE", testId: "manager-self-leave-nav" },
  { path: "/employee/regularization", label: "My Regularization", shortLabel: "FIX", testId: "manager-self-regularization-nav" },
  { path: "/employee/scan", label: "My QR Scan", shortLabel: "QR", testId: "manager-self-scan-nav" },
];


export default function ManagerPage({ section }) {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [teamMonitor, setTeamMonitor] = useState(null);
  const [approvals, setApprovals] = useState({ leave_requests: [], regularization_requests: [] });
  const [employees, setEmployees] = useState([]);

  const employeeMap = useMemo(() => Object.fromEntries(employees.map((employee) => [employee.id, employee])), [employees]);
  const navItems = useMemo(() => {
    const leaveEnabled = dashboard?.self_service?.leave_enabled ?? true;
    const quickActionFeature = dashboard?.self_service?.quick_action?.assigned_attendance_feature;
    return [...managerNavItems, ...managerSelfServiceNav.filter((item) => {
      if (item.path === "/employee/leave") return leaveEnabled;
      if (item.path === "/employee/scan") return ["kiosk_qr_attendance", "employee_qr_scan"].includes(quickActionFeature);
      return true;
    })];
  }, [dashboard]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (section === "dashboard") {
          const response = await api.get("/manager/dashboard");
          setDashboard(response.data);
        }
        if (section === "team") {
          const response = await api.get("/attendance/monitor");
          setTeamMonitor(response.data);
        }
        if (section === "approvals") {
          const [queue, team] = await Promise.all([api.get("/approvals/queue"), api.get("/employees")]);
          setApprovals(queue.data);
          setEmployees(team.data);
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [section]);

  const handleLeaveDecision = async (requestId, action) => {
    try {
      await api.put(`/leaves/requests/${requestId}/decision`, { action });
      toast.success(`Leave request ${action}`);
      const queue = await api.get("/approvals/queue");
      setApprovals(queue.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRegularizationDecision = async (requestId, action) => {
    try {
      await api.put(`/attendance/regularizations/${requestId}/decision`, { action });
      toast.success(`Regularization ${action}`);
      const queue = await api.get("/approvals/queue");
      setApprovals(queue.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const renderDashboard = () => (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="manager-dashboard-stats-grid">
        <StatCard label="Team Size" value={dashboard?.stats?.total_employees || 0} accent="#0D1B2A" helper="Direct reports in scope" testId="manager-stat-team-size" />
        <StatCard label="Present" value={dashboard?.stats?.present || 0} accent="#F4A300" helper="Team present today" testId="manager-stat-present" />
        <StatCard label="Late" value={dashboard?.stats?.late || 0} accent="#F59E0B" helper="Late teammates" testId="manager-stat-late" />
        <StatCard label="Absent" value={dashboard?.stats?.absent || 0} accent="#EF4444" helper="No attendance from team" testId="manager-stat-absent" />
        <StatCard label="Pending" value={dashboard?.stats?.pending_approvals || 0} accent="#0D1B2A" helper="Approvals awaiting action" testId="manager-stat-pending" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="border-slate-200/70 bg-white/85" data-testid="manager-late-list-card">
          <CardHeader>
            <CardTitle data-testid="manager-late-list-title">Late list</CardTitle>
            <CardDescription data-testid="manager-late-list-description">Immediate view of team members who crossed the grace period.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(dashboard?.late_list || []).map((row) => (
              <div key={row.employee_id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`manager-late-item-${row.employee_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-900">{row.employee_name}</p>
                  <StatusBadge value="Late" testId={`manager-late-badge-${row.employee_id}`} />
                </div>
                <p className="mt-2 text-sm text-slate-500">Late by {formatMinutes(row.late_minutes)} • {row.department_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="manager-absentees-card">
          <CardHeader>
            <CardTitle data-testid="manager-absentees-title">Absentees</CardTitle>
            <CardDescription data-testid="manager-absentees-description">Spot missing attendance and follow up with the team quickly.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(dashboard?.absentees || []).map((row) => (
              <div key={row.employee_id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`manager-absentee-item-${row.employee_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-900">{row.employee_name}</p>
                  <StatusBadge value="Absent" testId={`manager-absentee-badge-${row.employee_id}`} />
                </div>
                <p className="mt-2 text-sm text-slate-500">{row.team_name} • {row.department_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );

  const renderTeam = () => (
    <Card className="border-slate-200/70 bg-white/85" data-testid="manager-team-card">
      <CardHeader>
        <CardTitle data-testid="manager-team-title">Team attendance table</CardTitle>
        <CardDescription data-testid="manager-team-description">View status, hours, lateness, and overtime for each direct report.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="manager-team-table-wrap">
          <Table data-testid="manager-team-table">
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Late</TableHead>
                <TableHead>OT</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(teamMonitor?.rows || []).map((row) => (
                <TableRow key={row.employee_id} data-testid={`manager-team-row-${row.employee_id}`}>
                  <TableCell>{row.employee_name}</TableCell>
                  <TableCell><StatusBadge value={row.current_status} testId={`manager-team-status-${row.employee_id}`} /></TableCell>
                  <TableCell>{extractTime(row.record?.clock_in)}</TableCell>
                  <TableCell>{extractTime(row.record?.clock_out)}</TableCell>
                  <TableCell>{formatHours(row.total_hours)}</TableCell>
                  <TableCell>{formatMinutes(row.late_minutes)}</TableCell>
                  <TableCell>{formatMinutes(row.overtime_minutes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  const renderApprovals = () => (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card className="border-slate-200/70 bg-white/85" data-testid="manager-leave-approvals-card">
        <CardHeader>
          <CardTitle data-testid="manager-leave-approvals-title">Leave approvals</CardTitle>
          <CardDescription data-testid="manager-leave-approvals-description">Approve or reject only for team members in your scope.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {approvals.leave_requests.map((request) => (
            <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`manager-leave-item-${request.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-900">{employeeMap[request.employee_id]?.name || request.employee_id}</p>
                <StatusBadge value={request.status} testId={`manager-leave-status-${request.id}`} />
              </div>
              <p className="mt-2 text-sm text-slate-500">{request.leave_type_name} • {formatDate(request.start_date)} to {formatDate(request.end_date)}</p>
              <div className="mt-4 flex gap-2">
                <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => handleLeaveDecision(request.id, "approved")} data-testid={`manager-leave-approve-${request.id}`}>Approve</Button>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleLeaveDecision(request.id, "rejected")} data-testid={`manager-leave-reject-${request.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="manager-regularization-card">
        <CardHeader>
          <CardTitle data-testid="manager-regularization-title">Regularization approvals</CardTitle>
          <CardDescription data-testid="manager-regularization-description">Review requested time changes before updating attendance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {approvals.regularization_requests.map((request) => (
            <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`manager-regularization-item-${request.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-900">{employeeMap[request.employee_id]?.name || request.employee_id}</p>
                <StatusBadge value={request.status} testId={`manager-regularization-status-${request.id}`} />
              </div>
              <p className="mt-2 text-sm text-slate-500">{formatDate(request.date)} • {extractTime(request.requested_clock_in)} → {extractTime(request.requested_clock_out)}</p>
              <div className="mt-4 flex gap-2">
                <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => handleRegularizationDecision(request.id, "approved")} data-testid={`manager-regularization-approve-${request.id}`}>Approve</Button>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleRegularizationDecision(request.id, "rejected")} data-testid={`manager-regularization-reject-${request.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <AppShell title={section === "dashboard" ? "Team visibility" : section === "team" ? "Team attendance" : "Manager approvals"} subtitle={section === "dashboard" ? "See today’s team performance, late list, absentees, and pending approvals." : section === "team" ? "Monitor direct reports without crossing tenant boundaries." : "Approve leave and regularization for direct reports only."} navItems={navItems} user={user} tenant={tenant} onLogout={logout}>
      {loading ? <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="manager-loading-card">Loading manager workspace...</Card> : null}
      {!loading && section === "dashboard" ? renderDashboard() : null}
      {!loading && section === "team" ? renderTeam() : null}
      {!loading && section === "approvals" ? renderApprovals() : null}
    </AppShell>
  );
}